import React, { ReactNode } from 'react';
import { Box, Stack, keyframes } from '@mui/material';

import { IconButton } from '../IconButton';
import { Typography } from '../Typography';
import { Close24 as CloseIcon } from '../../icons/Close24';
import { Download24 as DownloadIcon } from '../../icons/Download24';
import { Loader24 as LoaderIcon } from '../../icons/Loader24';
import { FileError24 as ErrorIcon } from '../../icons/FileError24';
import { LinearProgress } from '../LinearProgress';
import { boxShadows, PaletteColor } from '../../palette';


export type AttachedFileState = 'default' | 'loading' | 'error';

export type AttachedFileProps = {
    title: ReactNode;
    extension?: string;
    size?: number;
    errorText?: ReactNode;
    state: AttachedFileState;
    onRemove?: () => void;
    onDownload?: () => void;
    disabled?: boolean;
};

const ICONS = {
    default: DownloadIcon,
    loading: LoaderIcon,
    error: ErrorIcon,
};

const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

type IconWrapperStyle = {
    backgroundColor: PaletteColor;
    color: PaletteColor;
    spin?: boolean;
};

const iconWrapperStylesByState: Record<AttachedFileState, IconWrapperStyle> = {
    default: {
        backgroundColor: 'infoCustom.green10',
        color: 'infoCustom.green50',
    },
    loading: {
        backgroundColor: 'infoCustom.green10',
        color: 'infoCustom.green50',
        spin: true,
    },
    error: {
        backgroundColor: 'infoCustom.red10',
        color: 'infoCustom.red50',
    },
};

const formatSizeRu = (size: number): string => {
    const kb = 1024;
    const mb = 1024 * 1024;

    if (size >= mb)
        return `${(size / mb).toFixed(1).replace('.', ',')} Мб`;

    if (size >= kb)
        return `${(size / kb).toFixed(1).replace('.', ',')} Кб`;

    return `${size} Б`;
};

export const AttachedFile = React.memo<AttachedFileProps>(({
    state,
    title,
    extension,
    size,
    errorText,
    onRemove,
    onDownload,
    disabled = false,
}) => {
    const Icon = ICONS[state];
    const iconStyle = iconWrapperStylesByState[state];

    return (
        <Stack
            data-testid={`document-${state}`}
            onClick={onDownload}
            direction='row'
            alignItems='center'
            gap={4}
            width={268}
            py={3}
            px={4}
            borderRadius={2}
            bgcolor='grayscale.shade0'
            sx={{
                boxShadow: boxShadows.shadow1,
                '&:hover': {
                    boxShadow: '0px 8px 40px rgba(0, 0, 0, 0.14)',
                },
            }}
        >
            <Box
                width={40}
                height={40}
                minWidth={40}
                borderRadius='50%'
                bgcolor={iconStyle.backgroundColor}
                display='flex'
                justifyContent='center'
                alignItems='center'
                sx={{
                    animation: state === 'loading' && `${spin} 1s linear infinite`,
                    '& svg': {
                        color: iconStyle.color,
                        fontSize: 16,
                    },
                }}
            >
                <Icon />
            </Box>

            <Stack
                flex={1}
                minWidth={0}
                justifyContent='space-between'
                height='100%'
            >
                <Box
                    component='span'
                    display='inline-flex'
                    alignItems='baseline'
                    overflow='hidden'
                >
                    <Typography
                        variant='regularCaption1'
                        color='grayscale.shade100'
                        component='span'
                        noWrap
                    >
                        {title}
                    </Typography>
                    {extension && (
                        <Typography
                            variant='regularCaption1'
                            component='span'
                            color='grayscale.shade85'
                        >
                            {`.${extension.toLowerCase()}`}
                        </Typography>
                    )}
                </Box>

                {state === 'default' && (
                    <Typography
                        variant='regularCaption3'
                        color='grayscale.shade85'
                        component='div'
                        mt={0.5}
                    >
                        {size ? formatSizeRu(size) : ''}
                    </Typography>
                )}

                {state === 'loading' && (
                    <LinearProgress
                        variant='indeterminate'
                        color='infoCustom.green50'
                        sx={{ mt: 4 }}
                    />
                )}

                {state === 'error' && (
                    <Typography
                        variant='regularCaption3'
                        color='general.primary50'
                        component='div'
                        mt={0.5}
                    >
                        {errorText}
                    </Typography>
                )}
            </Stack>

            {onRemove && (
                <IconButton
                    disabled={disabled}
                    size='extraSmall'
                    variant='secondary'
                    isRound
                    onClick={e => {
                        e.stopPropagation();
                        e.currentTarget.blur();
                        onRemove();
                    }}
                    sx={{
                        '& svg': {
                            color: 'grayscale.shade80',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            )}
        </Stack>
    );
});
